const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const INSTALLATION_ID_KEY = 'frontend_browser_installation_id';
const REQUEST_ID_HEADER = 'X-Request-Id';

let initialized = false;

const normalizeBaseUrl = (baseUrl) => baseUrl.replace(/\/+$/, '');
const truncate = (value, max) => {
    if (value == null) {
        return value;
    }

    const stringValue = String(value);
    return stringValue.length > max ? stringValue.slice(0, max) : stringValue;
};

const getInstallationId = () => {
    const existing = localStorage.getItem(INSTALLATION_ID_KEY);
    if (existing) {
        return existing;
    }

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const value = Array.from(bytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    localStorage.setItem(INSTALLATION_ID_KEY, value);
    return value;
};

const getStoredUser = () => {
    try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};

const getAppVersion = () => process.env.REACT_APP_VERSION || process.env.npm_package_version || 'unknown';

const createRequestId = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getBrowserData = () => {
    const ua = navigator.userAgent || 'unknown';
    const browserName = navigator.userAgentData?.brands?.map((brand) => brand.brand).join(', ')
        || navigator.appName
        || 'browser';

    return {
        browserName,
        osVersion: ua,
    };
};

export const sendFrontendLog = async ({
    severity = 'info',
    eventType,
    message,
    errorType = null,
    endpoint = null,
    screen = null,
    stackTrace = null,
    requestId = null,
    metadata = {},
}) => {
    try {
        const token = localStorage.getItem('jwt_token');
        const user = getStoredUser();
        const { browserName, osVersion } = getBrowserData();

        const payload = {
            platform: 'web',
            client_service_name: truncate('react-web', 100),
            severity,
            event_type: truncate(eventType, 100),
            message: truncate(message, 4000),
            browser_installation_id: truncate(getInstallationId(), 191),
            app_version: truncate(getAppVersion(), 100),
            browser_name: truncate(browserName, 255),
            os_version: truncate(osVersion, 255),
            locale: truncate(navigator.language || 'en', 20),
            timezone: truncate(Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown', 100),
            screen: truncate(screen || window.location.pathname, 255),
            endpoint: truncate(endpoint, 255),
            error_type: truncate(errorType, 100),
            request_id: truncate(requestId, 191),
            stack_trace: truncate(stackTrace, 20000),
            metadata: {
                ...metadata,
                user_id: user?.id ?? null,
            },
        };

        await fetch(`${normalizeBaseUrl(API_URL)}/frontend/logs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
            keepalive: true,
        });
    } catch (_) {
        // Logging must never break the UI.
    }
};

export const initializeFrontendLogger = () => {
    if (initialized) {
        return;
    }

    initialized = true;

    window.addEventListener('error', (event) => {
        sendFrontendLog({
            severity: 'error',
            eventType: 'window_error',
            message: event.message || 'Unhandled window error',
            errorType: event.error?.name || 'window_error',
            stackTrace: event.error?.stack || null,
            metadata: {
                filename: event.filename || null,
                line: event.lineno || null,
                column: event.colno || null,
            },
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        sendFrontendLog({
            severity: 'error',
            eventType: 'unhandled_promise_rejection',
            message: reason?.message || String(reason || 'Unhandled promise rejection'),
            errorType: reason?.name || 'unhandled_promise_rejection',
            stackTrace: reason?.stack || null,
        });
    });
};

export const attachRequestId = (config) => {
    const requestId = createRequestId();
    config.headers = config.headers || {};
    config.headers[REQUEST_ID_HEADER] = requestId;
    config.metadata = {
        ...(config.metadata || {}),
        requestId,
        startedAt: Date.now(),
    };
    return config;
};

export const logHttpFailure = (error) => {
    const requestId = error?.config?.metadata?.requestId || error?.config?.headers?.[REQUEST_ID_HEADER] || null;
    const method = error?.config?.method?.toUpperCase() || 'UNKNOWN';
    const endpoint = error?.config?.url || null;
    const status = error?.response?.status || null;
    const durationMs = error?.config?.metadata?.startedAt
        ? Date.now() - error.config.metadata.startedAt
        : null;

    sendFrontendLog({
        severity: 'error',
        eventType: 'http_request_failed',
        message: `HTTP request failed: ${method} ${endpoint || ''}`.trim(),
        errorType: error?.name || 'http_error',
        endpoint,
        requestId,
        metadata: {
            method,
            status,
            duration_ms: durationMs,
            response_message: error?.response?.data?.message || null,
        },
    });
};
